(() => {
  const instances = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class AWAnnouncementBar {
    constructor(root) {
      this.root = root;
      this.items = Array.from(root.querySelectorAll('[data-aw-announcement-item]'));
      this.activeIndex = 0;
      this.timer = null;
      this.autoplay = root.dataset.autoplay === 'true';
      this.autoplaySeconds = Number.parseInt(root.dataset.autoplaySeconds || '5', 10);
      this.boundPause = this.pause.bind(this);
      this.boundResume = this.handleResume.bind(this);
    }

    init() {
      if (!this.items.length) {
        return;
      }

      this.setActive(0);
      this.root.addEventListener('mouseenter', this.boundPause);
      this.root.addEventListener('mouseleave', this.boundResume);
      this.root.addEventListener('focusin', this.boundPause);
      this.root.addEventListener('focusout', this.boundResume);
      this.start();
    }

    destroy() {
      this.stop();
      this.root.removeEventListener('mouseenter', this.boundPause);
      this.root.removeEventListener('mouseleave', this.boundResume);
      this.root.removeEventListener('focusin', this.boundPause);
      this.root.removeEventListener('focusout', this.boundResume);
    }

    canAutoplay() {
      return this.autoplay && this.items.length > 1 && !reducedMotion.matches;
    }

    setActive(index) {
      this.activeIndex = index;

      this.items.forEach((item, itemIndex) => {
        const isActive = itemIndex === index;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        item.querySelectorAll('a, button').forEach((element) => {
          if (isActive) {
            element.removeAttribute('tabindex');
          } else {
            element.setAttribute('tabindex', '-1');
          }
        });
      });
    }

    next() {
      if (!this.items.length) {
        return;
      }

      const nextIndex = (this.activeIndex + 1) % this.items.length;
      this.setActive(nextIndex);
    }

    start() {
      this.stop();

      if (!this.canAutoplay()) {
        return;
      }

      const interval = Math.max(3, this.autoplaySeconds) * 1000;
      this.timer = window.setInterval(() => {
        this.next();
      }, interval);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    pause() {
      this.stop();
    }

    handleResume(event) {
      if (event.type === 'focusout' && event.relatedTarget && this.root.contains(event.relatedTarget)) {
        return;
      }

      this.start();
    }

    openBlock(blockId) {
      if (!blockId) {
        return;
      }

      const index = this.items.findIndex((item) => item.getAttribute('data-block-id') === blockId);
      if (index === -1) {
        return;
      }

      this.pause();
      this.setActive(index);
    }
  }

  const init = (container = document) => {
    container.querySelectorAll('[data-aw-announcement-bar]').forEach((root) => {
      if (instances.has(root)) {
        return;
      }

      const instance = new AWAnnouncementBar(root);
      instance.init();
      instances.set(root, instance);
    });
  };

  const destroy = (container) => {
    if (!container) {
      return;
    }

    const root = container.matches?.('[data-aw-announcement-bar]')
      ? container
      : container.querySelector?.('[data-aw-announcement-bar]');
    if (!root) {
      return;
    }

    const instance = instances.get(root);
    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(root);
  };

  document.addEventListener('DOMContentLoaded', () => {
    init(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-aw-announcement-bar]');
    if (!root) {
      return;
    }

    const instance = instances.get(root);
    if (!instance) {
      return;
    }

    instance.openBlock(event.detail.blockId);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest('[data-aw-announcement-bar]');
    if (!root) {
      return;
    }

    const instance = instances.get(root);
    if (!instance) {
      return;
    }

    instance.start();
  });
})();
